import React, {
  createContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { storeSettings } from 'src/utils/settings';
import {
  themeModeFromDevice,
  normalizeThemeSettings,
  resolveEffectiveTheme,
} from 'src/utils/resolveTheme';

const SettingsContext = createContext();

function buildDefaultSettings() {
  const themeMode = themeModeFromDevice();
  return {
    direction: 'ltr',
    responsiveFontSizes: true,
    themeMode,
    theme: resolveEffectiveTheme(themeMode),
  };
}

export function SettingsProvider({ settings, children }) {
  const [currentSettings, setCurrentSettings] = useState(() => {
    const normalized = normalizeThemeSettings(settings || buildDefaultSettings());
    // Persist first visit + migrate legacy "system" to concrete light|dark.
    storeSettings(normalized);
    return normalized;
  });

  const handleSaveSettings = useCallback((updatedSettings = {}) => {
    setCurrentSettings((prev) => {
      const mergedSettings = normalizeThemeSettings(
        _.merge({}, prev, updatedSettings),
      );
      storeSettings(mergedSettings);
      return mergedSettings;
    });
  }, []);

  useEffect(() => {
    document.dir = currentSettings.direction;
  }, [currentSettings.direction]);

  return (
    <SettingsContext.Provider
      value={useMemo(() => ({
        settings: currentSettings,
        saveSettings: handleSaveSettings,
      }), [currentSettings, handleSaveSettings])}
    >
      {children}
    </SettingsContext.Provider>
  );
}

SettingsProvider.propTypes = {
  children: PropTypes.node.isRequired,
  settings: PropTypes.object
};

export const SettingsConsumer = SettingsContext.Consumer;

export default SettingsContext;
